import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { UserPlus, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, login } from "@/hooks/useAuth";
import { Avatar } from "@/components/friends/Avatar";

// Opened by someone who tapped their phone on an NFC sticker
export default function TapPage() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const { data: p, error } = useQuery({ queryKey: ["tap", token], queryFn: () => api.tapInfo(token), retry: false });
  const mine = user && p && user.user_id === p.user_id;
  const first = p?.name.split(" ")[0];
  const rel = sent ? "sent" : p?.relation;

  const add = async () => {
    if (!user) {
      localStorage.setItem("bl_tap", token);
      return login();
    }
    setBusy(true);
    try {
      const r = await api.tapRequest(token);
      if (r.relation === "friend") {
        toast.success(`You and ${first} are now friends`);
        return navigate(`/friend/${r.friend_id}`);
      }
      setSent(true);
      toast.success(`Friend request sent to ${first}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the request"); }
    setBusy(false);
  };
  useEffect(() => { document.title = p ? `${p.name} · the bucketlist` : "the bucketlist"; }, [p]);

  if (error) return <Shell><p className="text-slate-300" data-testid="tap-off">{error?.response?.data?.detail || "This sticker isn't active any more."}</p></Shell>;
  if (!p) return <Shell><div className="h-40 animate-pulse rounded-2xl bg-white/5" /></Shell>;
  return (
    <Shell>
      <div className="flex items-center gap-4" data-testid="tap-person">
        <Avatar f={p} size="w-16 h-16 text-2xl" />
        <div>
          <p className="eyebrow">You tapped</p>
          <h1 className="font-display text-3xl font-bold" data-testid="tap-name">{p.name}</h1>
        </div>
      </div>
      <div className="mt-8 grid gap-3">
        {mine && <p className="text-sm text-slate-400" data-testid="tap-own">This is your own sticker — others tap it to send you a friend request.</p>}
        {!mine && rel === "sent" && <p className="flex items-center gap-2 text-emerald-300" data-testid="tap-sent"><Check size={18} /> Request sent — you'll be friends once {first} accepts.</p>}
        {!mine && rel === "friend" && <Link to={`/friend/${p.user_id}`} className="flex items-center justify-center gap-2 rounded-full bg-white/10 px-5 py-3 font-medium hover:bg-white/15 transition-colors" data-testid="tap-view-friend"><Check size={18} /> You're friends — see {first}'s bucket list</Link>}
        {!mine && rel !== "sent" && rel !== "friend" && <button onClick={add} disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-emerald-400 px-5 py-3 font-semibold text-[#06121c] hover:bg-emerald-300 transition-colors disabled:opacity-60" data-testid="tap-add-friend">
          <UserPlus size={18} /> {user ? `Add ${first} as a friend` : "Make my bucket list & add friend"}
        </button>}
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <main className="min-h-screen bg-[#0B0F17] text-slate-100 px-6 py-16 flex items-center justify-center" data-testid="tap-page">
    <div className="w-full max-w-md">
      {children}
      <Link to="/" className="mt-10 inline-block text-sm text-emerald-300 hover:text-emerald-200" data-testid="tap-home-link">Open the bucketlist →</Link>
    </div>
  </main>
);
